import { emailValidator as identityEmailValidator } from '@havenengineering/module-shared-email-validation';

import { TITLE_OPTIONS } from '../components/forms/utils';
import { errors } from './errors';
import { StoredIdentityInfo } from './server-side-properties';

const PHONE_NUMBER_VALIDATION_REGEXP = /^(\d ?){10,14}$/;
const NAME_VALIDATION_REGEXP = /^[a-zA-Z '-]{1,30}$/;
const UK_POSTCODE_VALIDATION_REGEXP =
  /^(GIR {0,1}0AA|[A-PR-UWYZ]([0-9]{1,2}|([A-HK-Y][0-9]|[A-HK-Y][0-9]([0-9]|[ABEHMNPRV-Y]))|[0-9][A-HJKPS-UW]) {0,1}[0-9][ABD-HJLNP-UW-Z]{2})$/i;
const OWNER_ACCOUNT_NUMBER_REGEXP = /^[A-Z]{2}[0-9]{6}$/i;
const MAX_LENGTH_8_VALIDATION_REGEXP = /^.{1,8}$/;
const ADDRESS_LINE_VALIDATION_REGEXP = /^[a-zA-Z0-9 '\-,./]{1,50}$/;
const CITY_VALIDATION_REGEXP = /^[a-zA-Z '\-.,]{1,20}$/;
const COUNTY_VALIDATION_REGEXP = /^[a-zA-Z '-]{1,20}$/;
const BASIC_EMAIL_REGEXP = /^[a-zA-Z0-9].*@[a-zA-Z0-9].*\.([a-zA-Z]*)$/;

export const emailValidator = (value: string) => {
  const check = identityEmailValidator.validate(value);
  return check.valid ? undefined : errors.invalidEmail.message;
};

const regExpValidator = (regexp: RegExp, errorMessage: string) => (value: string) => {
  if (!value || regexp.test(value)) return undefined;
  return errorMessage;
};

export const weakEmailValidator = regExpValidator(BASIC_EMAIL_REGEXP, errors.invalidEmail.message);

export const normaliseCurlyApostrophes = (value: string) =>
  value.replace(/[\u2018\u2019\u02BC]/g, "'");

export const nameValidator = (value: string) => {
  if (value && !/[a-zA-Z]/.test(value)) {
    return errors.invalidNameLetters.message;
  }
  if (value && value.length > 30) {
    return errors.invalidNameLength.message;
  }
  return regExpValidator(NAME_VALIDATION_REGEXP, errors.invalidNameCharacters.message)(value);
};

export const ownerNameValidator = (value: string) =>
  nameValidator(normaliseCurlyApostrophes(value));

export const phoneNumberValidator = regExpValidator(
  PHONE_NUMBER_VALIDATION_REGEXP,
  errors.invalidPhone.message,
);
export const ukPostcodeValidator = regExpValidator(
  UK_POSTCODE_VALIDATION_REGEXP,
  errors.invalidPostcode.message,
);

export const ownerAccountNumberValidator = regExpValidator(
  OWNER_ACCOUNT_NUMBER_REGEXP,
  errors.invalidOwnerAccountNumber.message,
);

export const ownerPostcodeLengthValidator = regExpValidator(
  MAX_LENGTH_8_VALIDATION_REGEXP,
  errors.invalidOwnersPostcodeLength.message,
);

export const addressLineValidator = regExpValidator(
  ADDRESS_LINE_VALIDATION_REGEXP,
  errors.invalidAddressLine.message,
);

export const cityValidator = regExpValidator(CITY_VALIDATION_REGEXP, errors.invalidCity.message);

export const countyValidator = regExpValidator(
  COUNTY_VALIDATION_REGEXP,
  errors.invalidCounty.message,
);

export const isValidIdentityUpdate = (storedIdentity?: StoredIdentityInfo): boolean => {
  if (!storedIdentity) return false;

  const { firstName, lastName, title } = storedIdentity;

  if (!firstName || !NAME_VALIDATION_REGEXP.test(firstName)) return false;
  if (!lastName || !NAME_VALIDATION_REGEXP.test(lastName)) return false;
  if (!title || !isValidTitle(title.trim())) return false;

  return true;
};

const isValidTitle = (title: string): boolean => TITLE_OPTIONS.some(({ value }) => title === value);

export const titleValidator = (title: string | null) => {
  if (!title) return 'Please select a title';
  return isValidTitle(title) ? undefined : `${title} is not a valid title`;
};
